<template>
  <b-modal
    ref="modal"
    size="xl"
    hide-footer
    no-close-on-backdrop
    body-class="p-0"
    @shown="onShown"
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="d-flex align-items-center">
          <div class="dc-modal-icon mr-3">
            <i class="fa fa-barcode"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold">Conferencia de Devolucao #{{ devolucaoId }}</h5>
            <small class="text-muted">{{ cabecalho.CLIENTE_NOME || 'Cliente nao informado' }} - Venda #{{ cabecalho.VENDA_ID || '-' }}</small>
          </div>
        </div>
        <b-button size="sm" variant="link" class="text-muted" @click="close()">
          <i class="fa fa-times fa-lg"></i>
        </b-button>
      </div>
    </template>

    <b-overlay :show="loading" rounded="sm">
      <div class="dc-modal-body">
        <div class="dc-scan-panel">
          <div class="dc-scan-header">
            <div>
              <div class="dc-scan-label">Leitura de etiqueta</div>
              <small class="text-muted">Bipe somente etiquetas da venda original que voltaram fisicamente.</small>
            </div>
            <b-badge :variant="cabecalho.STATUS === '3' ? 'primary' : 'info'" pill>
              {{ cabecalho.STATUS === '3' ? 'Em conferencia' : 'Aguardando' }}
            </b-badge>
          </div>

          <div class="dc-scan-input-wrap">
            <i class="fa fa-barcode dc-scan-input-icon"></i>
            <input
              ref="barcodeInput"
              v-model="barcode"
              class="dc-scan-input"
              placeholder="Escaneie a etiqueta devolvida..."
              autocomplete="off"
              :disabled="loading || loadingColeta"
              @keyup.enter="registrarColeta"
            />
            <b-spinner v-if="loadingColeta" small variant="primary" class="dc-scan-spinner" />
          </div>

          <div v-if="ultimaEtiqueta" class="dc-last-scan">
            <i class="fa fa-check-circle text-success mr-2"></i>
            Ultima etiqueta: <strong>{{ ultimaEtiqueta.PRODUTO }}</strong>
            <span class="ml-2">{{ formatarQuantidade(ultimaEtiqueta.PESO) }} kg</span>
          </div>
        </div>

        <div class="dc-summary-grid">
          <div class="dc-summary-card">
            <small>Volumes previstos</small>
            <strong>{{ formatarQuantidade(totalPecasNfe, 0) }}</strong>
          </div>
          <div class="dc-summary-card">
            <small>Etiquetas bipadas</small>
            <strong>{{ etiquetasColetadas.length }}</strong>
          </div>
          <div class="dc-summary-card">
            <small>Pecas conferidas</small>
            <strong>{{ formatarQuantidade(totalPecasRecepcao, 0) }}</strong>
          </div>
          <div class="dc-summary-card">
            <small>Peso conferido</small>
            <strong>{{ formatarQuantidade(totalRecepcao) }}</strong>
          </div>
        </div>

        <b-row>
          <b-col lg="8" class="mb-3 mb-lg-0">
            <div class="dc-section">
              <div class="dc-section-title">
                <i class="fa fa-list-alt mr-2 text-primary"></i>
                Resumo por produto
              </div>
              <b-table
                :items="itens"
                :fields="fieldsItens"
                responsive
                small
                hover
                show-empty
                empty-text="Nenhum item com volumes para conferencia"
                class="mb-0"
              >
                <template #cell(PRODUTO)="data">
                  <div class="font-weight-bold">{{ data.item.PRODUTO_ID }} - {{ data.item.PRODUTO_NOME }}</div>
                  <small class="text-muted">Venda: {{ formatarQuantidade(data.item.QUANT_VENDA) }} kg / {{ formatarQuantidade(data.item.PECAS_VENDA, 0) }} pecas</small>
                </template>

                <template #cell(PREVISTO)="data">
                  <div class="font-weight-bold">{{ formatarQuantidade(data.item.PECAS_DEVOLVIDO_NFE, 0) }}</div>
                  <small class="text-muted">{{ formatarQuantidade(data.item.QUANT_DEVOLVIDO_NFE) }} kg</small>
                </template>

                <template #cell(CONFERIDO)="data">
                  <div :class="classeConferido(data.item)">
                    {{ formatarQuantidade(data.item.TOTAL_ETIQUETAS_COLETADAS, 0) }} etiqueta(s)
                  </div>
                  <small class="text-muted">{{ formatarQuantidade(data.item.QUANT_RECEPCAO) }} kg / {{ formatarQuantidade(data.item.PECAS_RECEPCAO, 0) }} pecas</small>
                </template>

                <template #cell(PROGRESSO)="data">
                  <div class="d-flex align-items-center">
                    <b-progress
                      :value="progressoItem(data.item)"
                      max="100"
                      height="8px"
                      class="flex-grow-1 mr-2"
                      :variant="progressoItem(data.item) >= 100 ? 'success' : 'primary'"
                    />
                    <span class="text-muted small">{{ progressoItem(data.item) }}%</span>
                  </div>
                </template>
              </b-table>
            </div>

            <div class="dc-section mt-3">
              <div class="dc-section-title">
                <i class="fa fa-tags mr-2 text-primary"></i>
                Etiquetas da venda original
              </div>
              <b-table
                :items="etiquetas"
                :fields="fieldsEtiquetas"
                responsive
                small
                hover
                show-empty
                empty-text="Nenhuma etiqueta localizada para os volumes informados"
                class="mb-0"
              >
                <template #cell(CODIGO_BARRAS)="data">
                  <div class="font-weight-bold">{{ data.item.CODIGO_BARRAS }}</div>
                  <small class="text-muted">{{ data.item.DESCRICAO }}</small>
                </template>

                <template #cell(PESO)="data">
                  {{ formatarQuantidade(data.item.PESO) }} kg
                </template>

                <template #cell(PECAS)="data">
                  {{ formatarQuantidade(data.item.PECAS, 0) }}
                </template>

                <template #cell(OP_COLETADA)="data">
                  <b-badge :variant="isColetada(data.item) ? 'success' : 'light'">
                    {{ isColetada(data.item) ? 'Bipada' : 'Pendente' }}
                  </b-badge>
                </template>
              </b-table>
            </div>
          </b-col>

          <b-col lg="4">
            <div class="dc-log">
              <div class="dc-log-header">
                <span><i class="fa fa-history mr-2 text-warning"></i>Sequencia de leitura</span>
                <b-badge variant="warning" pill>{{ etiquetasColetadas.length }}</b-badge>
              </div>

              <div class="dc-log-body">
                <div v-if="etiquetasColetadas.length === 0" class="dc-empty">
                  <i class="fa fa-inbox fa-2x mb-2"></i>
                  <div>Nenhuma etiqueta bipada.</div>
                </div>
                <template v-else>
                  <div
                    v-for="(etiqueta, index) in etiquetasColetadasOrdenadas"
                    :key="etiqueta.ETIQUETA_MOVIMENTO_DEVOLUCAO_ID"
                    class="dc-log-item"
                  >
                    <div class="dc-log-seq">#{{ etiquetasColetadasOrdenadas.length - index }}</div>
                    <div class="dc-log-info">
                      <div class="font-weight-bold">{{ etiqueta.DESCRICAO }}</div>
                      <small class="text-muted">
                        {{ etiqueta.CODIGO_BARRAS }} - {{ formatarQuantidade(etiqueta.PESO) }} kg
                      </small>
                    </div>
                    <button
                      type="button"
                      class="dc-log-remove"
                      title="Remover bipagem"
                      :disabled="loadingColeta"
                      @click="removerEtiqueta(etiqueta)"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-overlay>

    <div class="p-3 bg-light border-top d-flex justify-content-between align-items-center">
      <div class="text-muted small">
        As etiquetas sao salvas automaticamente na devolucao. A situacao da etiqueta no estoque nao muda durante a bipagem.
      </div>
      <div>
        <b-button variant="outline-secondary" class="mr-2" @click="fechar">Fechar</b-button>
        <b-button variant="success" @click="finalizar" :disabled="loading || etiquetasColetadas.length === 0">
          <i class="fa fa-check-circle mr-1"></i> Finalizar conferencia
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Tools from '@/tools/tools.js'

export default {
  name: 'DevolucaoConferenciaModal',
  props: {
    devolucaoId: { type: [Number, String], required: true }
  },
  data() {
    return {
      loading: false,
      loadingColeta: false,
      barcode: '',
      cabecalho: {},
      itens: [],
      etiquetas: [],
      ultimaEtiqueta: null,
      fieldsItens: [
        { key: 'PRODUTO', label: 'Produto' },
        { key: 'PREVISTO', label: 'Volumes devolvidos', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'CONFERIDO', label: 'Conferido', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'PROGRESSO', label: 'Progresso', thStyle: { width: '190px' } }
      ],
      fieldsEtiquetas: [
        { key: 'CODIGO_BARRAS', label: 'Etiqueta' },
        { key: 'SITUACAO', label: 'Sit.' },
        { key: 'PESO', label: 'Peso', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'PECAS', label: 'Pecas', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'OP_COLETADA', label: 'Status', tdClass: 'text-right', thClass: 'text-right' }
      ]
    }
  },
  computed: {
    totalPecasNfe() {
      return this.itens.reduce((acc, item) => acc + (Number(item.PECAS_DEVOLVIDO_NFE) || 0), 0)
    },
    totalRecepcao() {
      return this.itens.reduce((acc, item) => acc + (Number(item.QUANT_RECEPCAO) || 0), 0)
    },
    totalPecasRecepcao() {
      return this.itens.reduce((acc, item) => acc + (Number(item.PECAS_RECEPCAO) || 0), 0)
    },
    etiquetasColetadas() {
      return this.etiquetas.filter(item => this.isColetada(item))
    },
    etiquetasColetadasOrdenadas() {
      return [...this.etiquetasColetadas].sort((a, b) => {
        const seqA = Number(a.SEQUENCIA_EMBARQUE) || 0
        const seqB = Number(b.SEQUENCIA_EMBARQUE) || 0
        return seqB - seqA
      })
    }
  },
  methods: {
    abrir() {
      this.$refs.modal.show()
      this.carregar()
    },
    fechar() {
      this.$refs.modal.hide()
    },
    onShown() {
      this.focarBarcode()
    },
    onHidden() {
      this.barcode = ''
      this.ultimaEtiqueta = null
      this.$emit('closed')
    },
    focarBarcode() {
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus()
      })
    },
    extrairErro(err) {
      const data = err && err.response && err.response.data
      if (data && typeof data === 'object') return data.message || data.erro || data.mensagem || JSON.stringify(data)
      return data || (err && err.message) || err
    },
    async carregar() {
      this.loading = true
      try {
        const response = await this.$http.get(`/api/protected/devolucao/conferencia/itens/${this.devolucaoId}`)
        this.cabecalho = response.data.devolucao || {}
        this.itens = response.data.itens || []
        this.etiquetas = response.data.etiquetas || []
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', this.extrairErro(err))
      } finally {
        this.loading = false
        this.focarBarcode()
      }
    },
    async registrarColeta() {
      const codigoBarras = String(this.barcode || '').trim()
      if (!codigoBarras || this.loadingColeta) return

      this.loadingColeta = true
      try {
        const response = await this.$http.post('/api/protected/devolucao/conferencia/registrar-coleta', {
          devolucaoId: this.devolucaoId,
          codigoBarras,
          usuario: this.$store.state.usuario.NOME
        })
        this.ultimaEtiqueta = response.data && response.data.etiqueta
        this.barcode = ''
        await this.carregar()
        this.$emit('atualizado')
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Etiqueta', 'danger', this.extrairErro(err))
        this.barcode = ''
      } finally {
        this.loadingColeta = false
        this.focarBarcode()
      }
    },
    async removerEtiqueta(etiqueta) {
      const movimentoId = etiqueta.ETIQUETA_MOVIMENTO_DEVOLUCAO_ID
      if (!movimentoId) return

      this.loadingColeta = true
      try {
        await this.$http.post('/api/protected/devolucao/conferencia/remover-coleta', {
          etiquetaMovimentoId: movimentoId,
          usuario: this.$store.state.usuario.NOME
        })
        await this.carregar()
        this.$emit('atualizado')
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', this.extrairErro(err))
      } finally {
        this.loadingColeta = false
        this.focarBarcode()
      }
    },
    async finalizar() {
      const confirmar = await this.$bvModal.msgBoxConfirm(
        `Deseja finalizar a conferencia da devolucao #${this.devolucaoId}?`,
        {
          title: 'Finalizar conferencia',
          okTitle: 'SIM, FINALIZAR',
          cancelTitle: 'CANCELAR',
          okVariant: 'success',
          centered: true
        }
      )
      if (!confirmar) return

      this.loading = true
      try {
        await this.$http.post('/api/protected/devolucao/conferencia/finalizar', {
          devolucaoId: this.devolucaoId,
          usuario: this.$store.state.usuario.NOME
        })
        Tools.exibirMensagem(this.$store, 'Conferencia', 'success', 'Conferencia finalizada.')
        this.$emit('concluido')
        this.fechar()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', this.extrairErro(err))
      } finally {
        this.loading = false
      }
    },
    isColetada(item) {
      return String(item.OP_COLETADA || '0') === '1' || !!item.ETIQUETA_MOVIMENTO_DEVOLUCAO_ID
    },
    progressoItem(item) {
      const previsto = Number(item.PECAS_DEVOLVIDO_NFE) || 0
      const conferido = Number(item.TOTAL_ETIQUETAS_COLETADAS) || 0
      if (previsto <= 0) return conferido > 0 ? 100 : 0
      return Math.min(100, Math.round((conferido / previsto) * 100))
    },
    classeConferido(item) {
      return this.progressoItem(item) >= 100 ? 'text-success font-weight-bold' : 'text-primary font-weight-bold'
    },
    formatarQuantidade(valor, casas = 3) {
      return (Number(valor) || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas
      })
    }
  }
}
</script>

<style scoped>
.dc-modal-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #0f766e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.dc-modal-body {
  background: #f8fafc;
  padding: 16px;
}

.dc-scan-panel,
.dc-section,
.dc-log,
.dc-summary-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.dc-scan-panel {
  padding: 16px;
  margin-bottom: 16px;
}

.dc-scan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.dc-scan-label,
.dc-section-title,
.dc-log-header {
  font-weight: 800;
  color: #0f172a;
}

.dc-scan-input-wrap {
  position: relative;
}

.dc-scan-input {
  width: 100%;
  height: 54px;
  border: 2px solid #0ea5e9;
  border-radius: 8px;
  padding: 0 46px;
  font-size: 18px;
  font-weight: 700;
  outline: none;
  background: #f8fbff;
}

.dc-scan-input:focus {
  border-color: #0284c7;
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.12);
}

.dc-scan-input-icon,
.dc-scan-spinner {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.dc-scan-input-icon {
  left: 16px;
  color: #0284c7;
}

.dc-scan-spinner {
  right: 16px;
}

.dc-last-scan {
  margin-top: 10px;
  color: #334155;
}

.dc-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.dc-summary-card {
  padding: 12px;
}

.dc-summary-card small {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.dc-summary-card strong {
  display: block;
  margin-top: 4px;
  font-size: 22px;
  color: #0f172a;
}

.dc-section-title {
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
}

.dc-log {
  overflow: hidden;
}

.dc-log-header {
  background: #1f2937;
  color: #fff;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dc-log-body {
  max-height: 520px;
  overflow: auto;
  padding: 10px;
}

.dc-empty {
  color: #94a3b8;
  text-align: center;
  padding: 40px 10px;
}

.dc-log-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
}

.dc-log-seq {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.dc-log-info {
  min-width: 0;
  flex: 1;
}

.dc-log-remove {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #fee2e2;
  color: #b91c1c;
}

.dc-log-remove:disabled {
  opacity: 0.5;
}

@media (max-width: 992px) {
  .dc-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>



// WEBPACK FOOTER //
// src/views/faturamento/devolucao/DevolucaoConferenciaModal.vue